import { StoreCollectionForm, StoreCollectionFormScriptingStatus } from './forms-state';
import { createEntityAdapter, Update } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { formsActions } from './forms-actions';
import { CollectionFormService } from '../../../features/bizzmine/form/services/collection-form.service';
import { DocumentCheckinType } from '../../../../models/ts/document-checkin-type.model';
import { viewStackActions } from './view-stack-actions';

export const formsAdapter = createEntityAdapter<StoreCollectionForm>();

export const formsReducer = createReducer(
  formsAdapter.getInitialState(),
  on(formsActions.formInitializationStatusChanged, (state, { formId, status }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      let updatedStateForm = structuredClone(stateForm);
      updatedStateForm.status.initialization = status;
      return formsAdapter.updateOne(
        { id: formId, changes: updatedStateForm },
        state
      );
    } else
      return state;
  }),
  on(formsActions.scriptingEvaluating, (state, { formId, onOpen }) => {
    const stateForm = state.entities[formId];
    if (stateForm && stateForm.status.scripting === StoreCollectionFormScriptingStatus.Idle) {
      let updatedStateForm = structuredClone(stateForm);
      updatedStateForm.status.scripting = StoreCollectionFormScriptingStatus.Evaluating;
      return formsAdapter.updateOne(
        { id: formId, changes: updatedStateForm },
        state
      );
    } else
      return state;
  }),
  on(formsActions.scriptingExecutingActions, (state, { formId }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      let updatedStateForm = structuredClone(stateForm);
      updatedStateForm.status.scripting = StoreCollectionFormScriptingStatus.ExecutingActions;
      return formsAdapter.updateOne(
        { id: formId, changes: updatedStateForm },
        state
      );
    } else
      return state;
  }),
  on(formsActions.scriptingComplete, (state, { formId }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      let updatedStateForm = structuredClone(stateForm);
      updatedStateForm.status.scripting = StoreCollectionFormScriptingStatus.Idle;
      return formsAdapter.updateOne(
        { id: formId, changes: updatedStateForm },
        state
      );
    } else
      return state;
  }),
  /**
   * Add form to store
   */
  on(formsActions.formFetched, formsActions.formFetchedWithFiles, formsActions.formFetchedWithTemplate, formsActions.formFetchedByWidgetIdWithCategory, formsActions.formFetchedWithProtectedField, (state, { form }) => {
    return formsAdapter.addOne(form, state);
  }),
  /**
   * Update entire form
   */
  on(formsActions.updateForm, formsActions.updateScriptingForm, (state, { update }) => {
    return formsAdapter.updateOne(update, state);
  }),
  on(formsActions.updateFormData, (state, { formId, data }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(data, formId, stateForm.status),
        state
      );
    } else
      return state;
  }),
  on(formsActions.updateTrainingDocumentFields, (state, { formId }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      const updateInfo = CollectionFormService.updateTrainingDocumentFields(
        structuredClone(stateForm.data),
        formId
      );
      if (!updateInfo.hasUpdates) {
        return state;
      }
      return formsAdapter.updateOne(
        updateInfo.update,
        state
      );
    } else
      return state;
  }),
  on(formsActions.mapComboboxValues, (state, { formId, gridFieldId, fieldId, options }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      return formsAdapter.updateOne(
        CollectionFormService.updateFieldOptions(
          structuredClone(stateForm.data),
          formId,
          fieldId,
          gridFieldId,
          options
        ),
        state
      );
    } else
      return state;
  }),
  /**
   * Update integrity state of a form
   */
  on(formsActions.updateIntegrity, (state, { formId, integrity }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdateIntegrity(
          structuredClone(stateForm.data),
          formId,
          integrity,
          stateForm.status
        ),
        state
      );
    } else
      return state;
  }),
  /**
   * Update scheduler data of a (scheduler) form
   */
  on(formsActions.updateScheduler, (state, { formId, scheduler }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdateScheduler(
          structuredClone(stateForm.data),
          formId,
          scheduler,
          stateForm.status
        ),
        state
      );
    } else
      return state;
  }),
  /**
   * Show Loading
   */
  on(
    formsActions.createLinkedFormInstance,
    formsActions.saveLinkedFormInstance,
    formsActions.deleteSelfLock,
    formsActions.editReadOnlyForm,
    formsActions.deleteLock,
    formsActions.addEmptyRowToGrid,
    formsActions.addRowsToGrid,
    formsActions.refreshForm,
    formsActions.getGridLinkedForm,
    formsActions.getExternalGridLinkedForm,
    formsActions.getGridLinkedFormWithFile,
    formsActions.getGridLinkedFormWithTemplate,
    formsActions.getGridLinkedInstance,
    formsActions.executeTask,
    (state, { formId }) => {
      const stateForm = state.entities[formId];
      if (stateForm && !stateForm.loading) {
        return formsAdapter.updateOne(
          CollectionFormService.setLoading(stateForm, true),
          state
        );
      } else
        return state;
    }),
  on(formsActions.lookupChanged,
    (state, { formId, lookupFieldId, lookupItem, clearChildren, showFormLoader }) => {
      const stateForm = state.entities[formId];
      if (stateForm && !stateForm.loading && showFormLoader) {
        return formsAdapter.updateOne(
          CollectionFormService.setLoading(stateForm, true),
          state
        );
      }
      return state;
    }),
  /**
   * Clear Loading
   */
  on(
    formsActions.selfLockDeleted,
    formsActions.lockDeleted,
    formsActions.rowsAddedToGrid,
    formsActions.getEmptyRowForGridFailed,
    formsActions.createLinkedFormInstanceFailed,
    formsActions.saveLinkedFormInstanceFailed,
    formsActions.editReadOnlyFormFetched,
    formsActions.gridLookupFailed,
    formsActions.lookupFailed,
    formsActions.formGroupReset,
    formsActions.lookupFetched,
    formsActions.executeTaskSucceeded,
    formsActions.executeTaskFailed,
    formsActions.removeFormLoadingState,
    (state, { formId }) => {
      const stateForm = state.entities[formId];
      if (stateForm && stateForm.loading) {
        return formsAdapter.updateOne(
          CollectionFormService.setLoading(stateForm, false),
          state
        );
      } else
        return state;
    }),
  /**
   * Clear Parent Loading on close
   */
  on(formsActions.formClosed, (state, { formId }) => {
    const stateForm = state.entities[formId];
    if (stateForm?.options?.parentFormId !== undefined) {
      const parentStateForm = state.entities[stateForm.options.parentFormId];
      if (parentStateForm !== undefined && parentStateForm.loading)
        return formsAdapter.updateOne(
          CollectionFormService.setLoading(parentStateForm, false),
          state
        );
    }
    return state;
  }),
  /**
   * Clear Loading & Parent Loading on linked save
   */
  on(formsActions.createLinkedFormInstanceSucceeded, formsActions.createGridLinkedFormInstanceSucceeded, (state, {
    formId,
    parentFormId
  }) => {
    const stateForm = state.entities[formId];
    const parentStateForm = state.entities[parentFormId];
    const updates: Array<Update<StoreCollectionForm>> = [];
    if (stateForm)
      updates.push(CollectionFormService.setLoading(stateForm, false));
    if (parentStateForm)
      updates.push(CollectionFormService.setLoading(parentStateForm, false));
    if (updates.length > 0)
      return formsAdapter.updateMany(
        updates,
        state
      ); else return state;
  }),
  /**
   * Remove from store
   */
  on(viewStackActions.removeFormFromViewStack, (state, { formId }) => {
    return formsAdapter.removeOne(formId, state);
  }),
  on(viewStackActions.removeAllFormsFromViewStack, (state) => {
    return formsAdapter.removeAll(state);
  }),
  /**
   * Update Org Chart
   */
  on(
    formsActions.updateOrgChartFieldValue,
    formsActions.updateRegisteredOnBehalfOfFieldValueWithEntity,
    (state, {
      formId,
      fieldId,
      value
    }) => {
      const stateForm = state.entities[formId];
      if (stateForm)
        return formsAdapter.updateOne(
          CollectionFormService.toStoreUpdate(
            CollectionFormService.setOrgChartValue(
              structuredClone(stateForm.data),
              fieldId,
              value
            ),
            formId,
            stateForm.status
          ),
          state
        );
      else return state;
    }),
  /**
   * Update Enum
   */
  on(formsActions.updateFormFieldEnumValue, (state, { formId, fieldId, value }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setFieldEnumValue(
            structuredClone(stateForm.data),
            fieldId,
            value
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),
  /**
   * Update CheckOut
   */
  on(formsActions.updateFormCheckedOutBy, (state, { formId, checkOutByUserId }) => {
      const stateForm = state.entities[formId];
      if (stateForm) {
        const current = structuredClone(stateForm.data);
        current.DocumentProperties.CheckedOutByID = checkOutByUserId;
        current.DocumentProperties.DocumentCheckinStatus = DocumentCheckinType.CheckedOut;
        return formsAdapter.updateOne(
          CollectionFormService.toStoreUpdate(
            current,
            formId,
            stateForm.status
          ),
          state
        );
      } else return state;
    }
  ),
  /**
   * Update CheckOut
   */
  on(formsActions.updateFormCheckedIn, (state, { formId, file }) => {
      const stateForm = state.entities[formId];
      if (stateForm) {
        const current = structuredClone(stateForm.data);
        current.DocumentProperties.CheckedOutByID = 0;
        current.DocumentProperties.DocumentCheckinStatus = DocumentCheckinType.Uploaded;
        current.DocumentProperties.File = file.id;
        current.DocumentProperties.Extension = file.extension;
        current.DocumentProperties.Title = file.title;
        current.DocumentProperties.Size = file.size;
        return formsAdapter.updateOne(
          CollectionFormService.toStoreUpdate(
            current,
            formId,
            stateForm.status
          ),
          state
        );
      } else return state;
    }
  ),
  /**
   * Update Field Value
   */
  on(formsActions.updateFormFieldValue, (state, { formId, fieldId, value }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setFieldValue(
            structuredClone(stateForm.data),
            fieldId,
            value
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),
  on(formsActions.updateFormFieldSubscribedUsers, (state, { formId, fieldId, orgChart, users, deletedUsers }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setFieldSubscriberData(
            structuredClone(stateForm.data),
            fieldId,
            orgChart,
            users,
            deletedUsers
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),
  on(formsActions.updateTrainingAssessment, (state, { formId, trainingAssessment }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setTrainingAssessment(
            structuredClone(stateForm.data),
            trainingAssessment
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),

  /**
   * Update Field Value
   */
  on(formsActions.updateFormFieldCaption, (state, { formId, fieldId, value }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setFieldCaption(
            structuredClone(stateForm.data),
            fieldId,
            value
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),
  on(formsActions.fetchAiButtonData, (state, { formId, field }) => {
    const stateForm = state.entities[formId];
    console.debug(field.AiTargetFields);
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setFieldsLoading(
            structuredClone(stateForm.data),
            field.AiTargetFields.map(f => CollectionFormService.getFormFieldIdForAiTarget(stateForm.data, f)),
            true
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),
  on(formsActions.fetchAiButtonDataSucceeded, formsActions.fetchAiButtonDataFailed, (state, { formId, field }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setFieldsLoading(
            structuredClone(stateForm.data),
            field.AiTargetFields.map(f => CollectionFormService.getFormFieldIdForAiTarget(stateForm.data, f)),
            false
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),
  on(formsActions.checkAiConfirmation, (state, { formId }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      let updatedStateForm = CollectionFormService.updateAiGenerationConfirmation(structuredClone(stateForm));
      return formsAdapter.updateOne(
        { id: formId, changes: updatedStateForm },
        state
      );
    } else return state;
  }),
  on(formsActions.setAiUnsavedChanges, (state, { formId, unsavedChanges}) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      let updatedStateForm = CollectionFormService.setAiUnsavedChanges(structuredClone(stateForm), unsavedChanges);
      return formsAdapter.updateOne(
        { id: formId, changes: updatedStateForm },
        state
      );
    } else return state;
  }),
  on(formsActions.acceptAllAiGeneration, (state, { formId }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      const update = CollectionFormService.toStoreUpdate(
        CollectionFormService.confirmAiGenerationForForm(
          structuredClone(stateForm.data)
        ),
        formId,
        stateForm.status
      );
      // Also mark the whole form as AI all accepted
      update.changes = { ...update.changes, aiAllAccepted: true };
      return formsAdapter.updateOne(update, state);
    } else return state;
  }),
  on(formsActions.updateFormFieldAiGeneration, (state, { formId, fieldId, gridFieldId, recordId, value }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setFieldAiGeneration(
            structuredClone(stateForm.data),
            fieldId,
            gridFieldId,
            recordId,
            value,
            true
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),
  on(formsActions.updateFormAllFieldAiGeneration, (state, { formId, fieldId }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setFieldAllAiGeneration(
            structuredClone(stateForm.data),
            fieldId
          ),
          formId,
          stateForm.status
        ),
        state
      );
    } else return state;
  }),

  on(formsActions.updateAiLoading, (state, { formId, fieldId, aiLoading }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setFieldAiLoading(
            structuredClone(stateForm.data),
            fieldId,
            aiLoading,
            true
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),

  on(formsActions.updateAiLoadingInGrid, (state, { formId, fieldId, recordId, gridFieldId, aiLoading }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setFieldAiLoadingInGrid(
            structuredClone(stateForm.data),
            fieldId,
            gridFieldId,
            recordId,
            aiLoading
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),
  /**
   * Update Grid field value
   */
  on(formsActions.updateGridFormFieldValue, (state, { formId, gridFieldId, recordId, recordFieldId, value }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setGridFieldValue(
            structuredClone(stateForm.data),
            gridFieldId,
            recordId,
            recordFieldId,
            value
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),
  /**
   * Update Grid field records
   */
  on(formsActions.updateGridFormFieldRecords, (state, { formId, gridFieldId, records }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setGridFieldRecords(
            structuredClone(stateForm.data),
            gridFieldId,
            records
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),
  /**
   * Update Org Chart in Grid
   */
  on(formsActions.updateGridOrgChartFieldValue, (state, { formId, gridFieldId, recordId, recordFieldId, value }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setGridFieldOrgChartValue(
            structuredClone(stateForm.data),
            gridFieldId,
            recordId,
            recordFieldId,
            value
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),
  /**
   * Update result of Formula
   */
  on(formsActions.updateFormulaOutputField, (state, { formId, fieldId, value }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setFieldValuePredicate(
            structuredClone(stateForm.data),
            field => field.CollectionFieldsID == fieldId,
            value
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),
  /**
   * update result of formula (in a grid)
   */
  on(formsActions.updateFormulaOutputGridField, (state, {
    formId,
    gridFieldId,
    recordId,
    fieldId,
    value
  }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setGridFieldValue(
            structuredClone(stateForm.data),
            gridFieldId,
            recordId,
            fieldId,
            value
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),

  on(formsActions.setFieldProperty, (state, { formId, fieldId, property, value }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setFieldPropertyValue(
            structuredClone(stateForm.data),
            fieldId,
            property,
            value
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),

  /**
   * Update IsReadOnly property of a field
   */
  on(formsActions.setFieldReadOnly, (state, { formId, formFieldId, isReadOnly, readOnlyPriority }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setFieldReadOnly(
            structuredClone(stateForm.data),
            formFieldId,
            isReadOnly,
            readOnlyPriority
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),

  on(formsActions.setFieldLoading, (state, { formId, formFieldId, isLoading }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setFieldLoading(
            structuredClone(stateForm.data),
            formFieldId,
            isLoading
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),

  on(formsActions.setFieldsLoading, (state, { formId, formFieldIds, isLoading }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setFieldsLoading(
            structuredClone(stateForm.data),
            formFieldIds,
            isLoading
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),

  /**
   * Update IsReadOnly property of a Grid field
   */
  on(formsActions.setGridFieldReadOnly, (state, {
    formId,
    fieldId,
    recordId,
    recordFieldId,
    isReadOnly,
    readOnlyPriority
  }) => {
    const stateForm = state.entities[formId];
    if (stateForm)
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setGridFieldReadOnly(
            structuredClone(stateForm.data),
            fieldId,
            recordId,
            recordFieldId,
            isReadOnly,
            readOnlyPriority
          ),
          formId,
          stateForm.status
        ),
        state
      );
    else return state;
  }),

  /**
   * Update single VDS
   */
  on(formsActions.updateFormViewDataSource, (state, { formId, viewDataSource }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      const updatedForm = structuredClone(stateForm.data);
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(CollectionFormService.updateViewDataSource(updatedForm, viewDataSource), formId, stateForm.status),
        state
      );
    } else return state;
  }),
  /**
   * Update multiple VDS's
   */
  on(formsActions.updateFormViewDataSources, (state, { formId, viewDataSources }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      const updatedForm = structuredClone(stateForm.data);
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(CollectionFormService.updateViewDataSources(updatedForm, viewDataSources), formId, stateForm.status),
        state
      );
    } else return state;
  }),
  /**
   * Add valdiationErrors to form
   */
  on(formsActions.formValidationFailed, (state, { formId, validationErrors }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      const updatedForm = structuredClone(stateForm);
      updatedForm.validationResult = validationErrors;
      return formsAdapter.updateOne({ id: formId, changes: updatedForm }, state);
    } else return state;
  }),
  /**
   * Clear validation errors from form
   */
  on(formsActions.clearFormValidation, (state, { formId }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      const updatedForm = structuredClone(stateForm);
      updatedForm.validationResult = undefined;
      updatedForm.aiAllAccepted = false;
      return formsAdapter.updateOne({ id: formId, changes: updatedForm }, state);
    } else return state;
  }),

  /**
   * Clear validation errors from a single form field
   */
  on(formsActions.clearFormFieldValidation, (state, { formId, fieldId }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      return formsAdapter.updateOne({
        id: formId,
        changes: CollectionFormService.clearFormFieldValidation(structuredClone(stateForm), fieldId)
      }, state);
    } else return state;
  }),
  /**
   * Clear reset boolean marker from form
   */
  on(formsActions.formGroupReset, (state, { formId }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      const updatedState = structuredClone(stateForm);
      updatedState.reset = false;
      return formsAdapter.updateOne(
        { id: formId, changes: updatedState },
        state
      );
    } else
      return state;
  }),
  /**
   * Add records to a grid
   */
  on(formsActions.addRowsToGrid, (state, { formId, gridFieldId, records }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      const updatedState = structuredClone(stateForm);
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.addRecords(updatedState.data, gridFieldId, records),
          formId,
          stateForm.status
        ),
        state
      );
    } else
      return state;
  }),
  /**
   * Remove records from a grid
   */
  on(formsActions.removeRecordFromGrid, (state, { formId, gridFieldId, recordId }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      const updatedState = structuredClone(stateForm);
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.removeRecord(updatedState.data, gridFieldId, recordId),
          formId,
          stateForm.status
        ),
        state
      );
    } else
      return state;
  }),
  on(formsActions.removeRecordsFromGrid, (state, { formId, gridFieldId, recordIds }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      const updatedState = structuredClone(stateForm);
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.removeRecords(updatedState.data, gridFieldId, recordIds),
          formId,
          stateForm.status
        ),
        state
      );
    } else
      return state;
  }),
  /**
   * Clear all records from a grid
   */
  on(formsActions.clearGrid, (state, { formId, gridFieldId }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      const updatedState = structuredClone(stateForm);
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.clearGrid(updatedState.data, gridFieldId),
          formId,
          stateForm.status
        ),
        state
      );
    } else
      return state;
  }),
  /**
   * Update a grid record with lookup data
   */
  on(formsActions.updateGridRecordWithLookup, (state, { formId, gridFieldId, recordId, lookupData, lookupItem }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      let form = CollectionFormService.updateRecordWithLookup(structuredClone(stateForm.data), gridFieldId, recordId, lookupData, lookupItem);
      if (lookupData.ViewDataSources.length > 0) {
        form = CollectionFormService.updateViewDataSources(form, lookupData.ViewDataSources);
      }
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          form,
          formId,
          stateForm.status
        ),
        state
      );
    }
    return state;
  }),
  /**
   * Update the EditMode of a grid record
   */
  on(formsActions.updateGridRecordEditMode, (state, { formId, gridFieldId, recordId, editMode }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setRecordEditMode(structuredClone(stateForm.data), gridFieldId, recordId, editMode),
          formId,
          stateForm.status
        ),
        state
      );
    }
    return state;
  }),
  on(formsActions.gridLookupCleared, (state, { formId, gridFieldId, recordId }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.clearRecordData(structuredClone(stateForm.data), gridFieldId, recordId),
          formId,
          stateForm.status
        ),
        state
      );
    }
    return state;
  }),
  on(formsActions.updateGridRecord, (state, { formId, viewDataSourceId, lookupData, recordId, newRecordId }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.updateGridRecord(structuredClone(stateForm.data), viewDataSourceId, lookupData, recordId, newRecordId),
          formId,
          stateForm.status
        ),
        state
      );
    }
    return state;
  }),
  on(formsActions.gridRecordDataLookupUpdated, (state, {
    formId,
    recordId,
    lookupFieldId,
    lookupData
  }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.updateRecordData(structuredClone(stateForm.data), lookupFieldId, recordId, lookupData),
          formId,
          stateForm.status
        ),
        state
      );
    }
    return state;
  }),
  /**
   * Update the instance ID's of a grid record
   */
  on(formsActions.updateGridRecordInstance, (state, { formId, gridFieldId, recordId, instanceId }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setRecordInstanceId(structuredClone(stateForm.data), gridFieldId, recordId, instanceId),
          formId,
          stateForm.status
        ),
        state
      );
    }
    return state;
  }),

  on(formsActions.hideAccordion, (state, { formId, accordionId, hide }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setAccordionHidden(structuredClone(stateForm.data), accordionId, hide),
          formId,
          stateForm.status
        ),
        state
      );
    }
    return state;
  }),

  on(formsActions.hideRow, (state, { formId, accordionId, rowId, hide }) => {
    const stateForm = state.entities[formId];
    if (stateForm) {
      return formsAdapter.updateOne(
        CollectionFormService.toStoreUpdate(
          CollectionFormService.setRowHidden(structuredClone(stateForm.data), accordionId, rowId, hide),
          formId,
          stateForm.status
        ),
        state
      );
    }
    return state;
  })


  /*  on(formsActions.getAiButtonData, (state, { formId, field,}) => {
      const stateForm = state.entities[formId];
      if (stateForm)
        return formsAdapter.updateOne(
          CollectionFormService.toStoreUpdate(
            CollectionFormService.setFieldLoading(
              structuredClone(stateForm.data),
              field.Id,
              true
            ),
            formId
          ),
          state
        );
      else return state;
    }),

    on(formsActions.getAiButtonDataFailed, (state, { formId, field}) => {
      const stateForm = state.entities[formId];
      if (stateForm)
        return formsAdapter.updateOne(
          CollectionFormService.toStoreUpdate(
            CollectionFormService.setFieldLoading(
              structuredClone(stateForm.data),
              field.Id,
              false
            ),
            formId
          ),
          state
        );
      else return state;
    }),

    on(formsActions.getAiButtonDataSucceeded, (state, { formId, field,}) => {
      const stateForm = state.entities[formId];
      if (stateForm)
        return formsAdapter.updateOne(
          CollectionFormService.toStoreUpdate(
            CollectionFormService.setFieldLoading(
              structuredClone(stateForm.data),
              field.Id,
              false
            ),
            formId
          ),
          state
        );
      else return state;
    }),*/
);
